import { adjustConcurrencyWithHysteresis } from "@/js/upload/concurrency";
import {
  CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS,
  CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS,
  CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT,
  CHUNKED_UPLOAD_HYSTERESIS_UP_PCT,
  CHUNKED_UPLOAD_MAX_IN_FLIGHT
} from "@/js/upload/config";
import {
  clearChunkedSession,
  getChunkedUploadKey,
  loadChunkedSession,
  saveChunkedSession
} from "@/js/upload/session";
import {
  createChunkedUploadSession,
  finalizeChunkedUpload,
  getChunkedUploadStatus,
  uploadChunk
} from "@/js/upload/api";
import { notifyProcessingStatus, waitForUploadCompletion } from "@/js/upload/status";

// Chunked upload flow with resumable sessions and adaptive concurrency.
const uploadFileChunked = async (seriesId, instanceUid, file, options = {}) => {
  const key = getChunkedUploadKey(seriesId, file, instanceUid);
  const resumeEnabled = options.resume !== false;
  const onStatus = typeof options.onStatus === "function" ? options.onStatus : null;
  let session = resumeEnabled ? loadChunkedSession(key) : null;
  let status = null;

  if (session && session.upload_id) {
    try {
      status = await getChunkedUploadStatus(session.upload_id);
    } catch (error) {
      clearChunkedSession(key);
      session = null;
    }
  }

  if (!session) {
    session = await createChunkedUploadSession(file, null, seriesId);
    saveChunkedSession(key, session);
  }

  if (!status && session && session.upload_id) {
    status = await getChunkedUploadStatus(session.upload_id);
  }

  const statusValue = status?.status;
  const shouldPollOnly =
    statusValue && ["queued", "ingesting"].includes(statusValue);
  const skipUpload = statusValue === "uploaded";
  if (statusValue === "completed") {
    clearChunkedSession(key);
    return;
  }

  const uploadId = session.upload_id;
  const chunkSize = status?.chunk_size || session.chunk_size;
  const totalChunks = status?.total_chunks || session.total_chunks;
  const received = new Set(status?.received_chunks || []);

  let throttled = false;
  if (!shouldPollOnly) {
    const pending = [];
    for (let i = 0; i < totalChunks; i++) {
      if (!received.has(i)) {
        pending.push(i);
      }
    }

    const onProgress =
      typeof options.onProgress === "function" ? options.onProgress : null;
    const chunkBytes = index =>
      Math.min(chunkSize, Math.max(0, file.size - index * chunkSize));

    let bytesSent = 0;
    received.forEach(index => {
      bytesSent += chunkBytes(index);
    });
    if (onProgress) {
      onProgress({ bytesSent, bytesTotal: file.size });
    }

    if (skipUpload) {
      bytesSent = file.size;
      if (onProgress) {
        onProgress({ bytesSent, bytesTotal: file.size });
      }
    } else {
      const speedSamples = [];
      const MAX_SPEED_SAMPLES = 5;
      let lastSpeedTime = performance.now();
      let lastSpeedBytes = bytesSent;
      let currentLimit = Math.min(1, CHUNKED_UPLOAD_MAX_IN_FLIGHT);
      let lastConcurrencyAdjust = 0;

      // Bidirectional tuning with hysteresis + cooldown to avoid oscillation.
      const updateConcurrencyFromSpeed = allowAdjust => {
        const now = performance.now();
        const elapsedSec = (now - lastSpeedTime) / 1000;
        const deltaBytes = bytesSent - lastSpeedBytes;
        if (elapsedSec <= 0 || deltaBytes <= 0) {
          return;
        }
        const mbps = (deltaBytes * 8) / (elapsedSec * 1024 * 1024);
        speedSamples.push(mbps);
        if (speedSamples.length > MAX_SPEED_SAMPLES) {
          speedSamples.shift();
        }
        const avgMbps =
          speedSamples.reduce((sum, v) => sum + v, 0) / speedSamples.length;
        if (
          allowAdjust &&
          now - lastConcurrencyAdjust >= CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS
        ) {
          const nextLimit = adjustConcurrencyWithHysteresis({
            avgMbps,
            currentLimit,
            maxLimit: CHUNKED_UPLOAD_MAX_IN_FLIGHT,
            thresholds: CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS,
            upPct: CHUNKED_UPLOAD_HYSTERESIS_UP_PCT,
            downPct: CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT
          });
          if (nextLimit !== currentLimit) {
            currentLimit = nextLimit;
            lastConcurrencyAdjust = now;
          }
        }
        lastSpeedTime = now;
        lastSpeedBytes = bytesSent;
      };

      // Upload chunks with adaptive concurrency; back off to 1 on first error.
      await new Promise((resolve, reject) => {
        let inFlight = 0;
        let nextIndex = 0;
        let recoveryMode = false;
        let recoveryTarget = currentLimit;
        let recoverySuccessStreak = 0;
        // Recover gradually after a failure: N consecutive chunks ok => +1 in-flight.
        const RECOVERY_SUCCESS_STREAK = 3;
        // Stop retrying a chunk after a few failures to avoid infinite loops.
        const MAX_CHUNK_RETRIES = 3;
        const retryCounts = new Map();

        const startNext = () => {
          while (inFlight < currentLimit && nextIndex < pending.length) {
            const chunkIndex = pending[nextIndex++];
            const start = chunkIndex * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const blob = file.slice(start, end);
            inFlight += 1;
            uploadChunk(uploadId, chunkIndex, blob)
              .then(() => {
                bytesSent += chunkBytes(chunkIndex);
                if (onProgress) {
                  onProgress({ bytesSent, bytesTotal: file.size });
                }
                updateConcurrencyFromSpeed(!recoveryMode);
                if (recoveryMode) {
                  recoverySuccessStreak += 1;
                  if (recoverySuccessStreak >= RECOVERY_SUCCESS_STREAK) {
                    const nextLimit = Math.min(
                      recoveryTarget,
                      currentLimit + 1
                    );
                    if (nextLimit > currentLimit) {
                      currentLimit = nextLimit;
                    }
                    recoverySuccessStreak = 0;
                    if (currentLimit >= recoveryTarget) {
                      recoveryMode = false;
                    }
                  }
                }
                inFlight -= 1;
                if (nextIndex >= pending.length && inFlight === 0) {
                  resolve();
                  return;
                }
                startNext();
              })
              .catch(error => {
                // Any upload failure forces backoff to a single in-flight chunk.
                const attempts = (retryCounts.get(chunkIndex) || 0) + 1;
                retryCounts.set(chunkIndex, attempts);
                if (attempts > MAX_CHUNK_RETRIES) {
                  inFlight -= 1;
                  reject(error);
                  return;
                }
                recoveryMode = true;
                recoveryTarget = Math.max(recoveryTarget, currentLimit);
                recoverySuccessStreak = 0;
                currentLimit = 1;
                inFlight -= 1;
                // Retry the failed chunk before moving ahead.
                pending.splice(nextIndex, 0, chunkIndex);
                startNext();
              });
          }
        };

        startNext();
      });
    }

    const finalizeResult = await finalizeChunkedUpload(uploadId);
    if (finalizeResult && finalizeResult._throttled) {
      throttled = true;
    }
  }

  await notifyProcessingStatus(uploadId, onStatus);
  await waitForUploadCompletion(uploadId, {
    retryFinalize: () => finalizeChunkedUpload(uploadId),
    throttled,
    onStatus
  });

  clearChunkedSession(key);
};

export { uploadFileChunked };
